/**
 * Utilidad central de RUT chileno para el frontend. Reemplaza las copias
 * locales que vivían en `SIIConnectionForm`, `CuentaBancariaPanel`,
 * `AddConnectionForm`, `EditConnectionForm` y `pages/admin/empresas/Detalle`.
 *
 * - `formatRut(value)` — input handler: pasa "12345678k" a "12.345.678-K".
 * - `cleanRut(value)` — quita puntos/guiones para enviar al backend.
 * - `isValidRut(value)` — chequea el dígito verificador (módulo 11). Las copias
 *   locales no validaban check digit, lo que dejaba pasar RUTs sintácticamente
 *   "bonitos" pero inválidos.
 */

export function formatRut(value: string): string {
  const cleaned = value.replace(/[^0-9kK]/g, '');
  if (cleaned.length === 0) return '';
  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1).toUpperCase();
  if (body.length === 0) return dv;
  const formatted = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${formatted}-${dv}`;
}

export function cleanRut(value: string): string {
  return value.replace(/[.\-\s]/g, '').toUpperCase();
}

/** Calcula el dígito verificador esperado para el cuerpo numérico del RUT. */
function computeCheckDigit(body: string): string {
  let sum = 0;
  let multiplier = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i], 10) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  const remainder = 11 - (sum % 11);
  if (remainder === 11) return '0';
  if (remainder === 10) return 'K';
  return String(remainder);
}

export function isValidRut(value: string): boolean {
  const cleaned = cleanRut(value);
  if (!/^\d{7,8}[0-9K]$/.test(cleaned)) return false;
  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1);
  return computeCheckDigit(body) === dv;
}
